import { apiGetCustomers } from "@lib/api/customer";
import {
  apiGetProviders,
  apiGetProviderTransactionConfigs,
} from "@lib/api/provider";
import {
  CustomerName,
  PaymentHistoryState,
  PaymentHistoryStatus,
  ProviderName,
  ProviderTransactionConfigName,
} from "@lib/components/MiniComponents/Others";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ROUTES } from "@lib/constants";
import { createYupSync } from "@lib/helpers/TableHelper";
import { transactionSchema } from "@lib/schemas/transaction";
import {
  AuthRoles,
  CurrencyEnum,
  TransactionProxyState,
  TransactionProxyStatus,
  TransactionType,
  UseMutationProps,
} from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Input, Select, Space, Typography } from "antd";
import { useCallback, memo, useMemo, useEffect } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  transaction?: TransactionType;
  transactionUpdateMutation: UseMutationProps<Partial<TransactionType>>;
  role: AuthRoles;
};

function AdminEditForm({
  transaction,
  transactionUpdateMutation,
  role,
}: Props) {
  const [form] = Form.useForm();
  const { messages } = useIntl();
  const customerId = Form.useWatch("customerId", form);
  const providerId = Form.useWatch("providerId", form);
  const providerTransactionConfigId = Form.useWatch(
    "providerTransactionConfigId",
    form,
  );
  const status = Form.useWatch("status", form);
  const state = Form.useWatch("state", form);

  useEffect(() => {
    if (transaction) {
      form.setFieldsValue(transaction);
    }
  }, [transaction]);

  const {
    data: customers,
    isLoading: customersLoading,
    isFetching: customersFetching,
  } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: role === AuthRoles.Admin && !!transaction,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: providers,
    isLoading: providersLoading,
    isFetching: providersFetching,
  } = useQuery({
    queryFn: () => apiGetProviders({ limit: 100 }),
    queryKey: ["providers-dropdown"],
    enabled: role === AuthRoles.Admin && !!transaction,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: providerTransactionConfigs,
    isLoading: providerTransactionConfigsLoading,
    isFetching: providerTransactionConfigsFetching,
  } = useQuery({
    queryFn: () =>
      apiGetProviderTransactionConfigs({
        limit: 100,
        join: ["provider"],
        filter: [`providerId||$eq||${providerId}`],
      }),
    queryKey: ["provider-transaction-configs-dropdown", providerId],
    enabled: role === AuthRoles.Admin && !!transaction && !!providerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { mutateAsync: updateTransaction, isPending: isUpdating } =
    transactionUpdateMutation;

  const handleReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  // Memoize options to prevent unnecessary re-renders
  const currencyOptions = useMemo(
    () =>
      Object.values(CurrencyEnum).map((currency) => ({
        label: currency,
        value: currency,
      })),
    [],
  );

  const statusOptions = useMemo(
    () =>
      Object.values(TransactionProxyStatus).map((status) => ({
        label: <PaymentHistoryStatus status={status} />,
        text: status,
        value: status,
      })),
    [],
  );

  const stateOptions = useMemo(
    () =>
      Object.values(TransactionProxyState).map((state) => ({
        label: <PaymentHistoryState state={state} />,
        text: state,
        value: state,
      })),
    [],
  );

  const customerOptions = useMemo(
    () =>
      customers?.data?.data?.map((customer) => ({
        label: <CustomerName customer={customer} />,
        text: customer.name,
        value: customer.id,
      })) || [],
    [customers],
  );

  const providerOptions = useMemo(
    () =>
      providers?.data?.data?.map((provider) => ({
        label: <ProviderName provider={provider} />,
        text: provider.name,
        value: provider.id,
      })) || [],
    [providers],
  );

  const configOptions = useMemo(
    () =>
      providerTransactionConfigs?.data?.data?.map((config) => ({
        label: <ProviderTransactionConfigName config={config} />,
        text: `${config.name} ${config?.from} ${config?.to} ${config?.currency}`,
        value: config.id,
      })) || [],
    [providerTransactionConfigs],
  );

  if (!transaction) return null;

  return (
    <Form<TransactionType>
      {...layout}
      form={form}
      name="admin-edit-form"
      onFinish={async (values) => {
        const data = {
          id: transaction?.id,
          ...values,
          amount: values.amount
            ? parseInt(values.amount.toString())
            : transaction?.amount,
        };
        if (data?.status === TransactionProxyStatus.Initialized) {
          data.status = TransactionProxyStatus.Pending;
        }
        await updateTransaction(data);
      }}
      style={{ maxWidth: 800 }}
      autoComplete="off"
      initialValues={{
        ...transaction,
      }}
    >
      <Form.Item
        name="amount"
        label={messages["common.amount"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
      >
        <Input
          type="number"
          addonAfter={
            <Form.Item name="currency" noStyle>
              <Select
                options={currencyOptions}
                placeholder={messages["common.selectOne"] as string}
              />
            </Form.Item>
          }
        />
      </Form.Item>

      <Form.Item
        name="customerId"
        label={messages["common.customer"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
      >
        <Select
          loading={!providers}
          options={customerOptions}
          showSearch
          optionFilterProp="text"
          value={customerId}
          onChange={(value) => {
            form.setFieldValue("customerId", value);
          }}
          disabled={customersLoading || customersFetching}
          placeholder={
            customersLoading || customersFetching
              ? (messages["common.loading"] as string)
              : (messages["common.selectOne"] as string)
          }
        />
      </Form.Item>

      <Form.Item
        name="providerId"
        label={messages["common.provider"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
      >
        <Select
          loading={!providers}
          options={providerOptions}
          showSearch
          optionFilterProp="text"
          value={providerId}
          onChange={(value) => {
            form.setFieldValue("providerId", value);
            if (status === TransactionProxyStatus.Initialized) {
              form.setFieldValue("status", TransactionProxyStatus.Pending);
              form.setFieldValue(
                "state",
                TransactionProxyState.PendingForAccountInfo,
              );
            }
          }}
          disabled={providersLoading || providersFetching}
          placeholder={
            providersLoading || providersFetching
              ? (messages["common.loading"] as string)
              : (messages["common.selectOne"] as string)
          }
        />
      </Form.Item>

      <Form.Item
        name="status"
        label={messages["common.status"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
      >
        <Select
          options={statusOptions}
          value={status}
          onChange={(value) => {
            form.setFieldValue("status", value);
          }}
          placeholder={messages["common.selectOne"] as string}
        />
      </Form.Item>

      <Form.Item
        name="state"
        label={messages["common.state"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
      >
        <Select
          options={stateOptions}
          value={state}
          onChange={(value) => {
            form.setFieldValue("state", value);
            if (
              value === TransactionProxyState.ApprovedByCustomer ||
              value === TransactionProxyState.ApprovedByProvider
            ) {
              form.setFieldValue("status", TransactionProxyStatus.Success);
            } else if (
              value === TransactionProxyState.PendingCustomerApproval ||
              value === TransactionProxyState.PendingProviderApproval
            ) {
              form.setFieldValue("status", TransactionProxyStatus.Pending);
            }
          }}
          placeholder={messages["common.selectOne"] as string}
        />
      </Form.Item>

      <Form.Item
        name="providerTransactionConfigId"
        label={messages["common.providerTransactionConfig"] as string}
        style={{ paddingBottom: 15 }}
        rules={[createYupSync({ schema: transactionSchema })]}
        help={
          <Typography.Text
            style={{ fontSize: 12, marginLeft: 8 }}
            type="secondary"
          >
            * {messages["helper.viewAllSettings"] as string}
            {" - "}
            <Link
              to={ROUTES.ADMIN.PAYMENT_PROVIDER.PAYMENT_TRANSACTION_CONFIG.LIST}
              target="_blank"
            >
              {/* You can view all the settings here. */}
              {messages["common.settings"] as string}
            </Link>
          </Typography.Text>
        }
      >
        <Select
          loading={
            providerTransactionConfigsLoading ||
            providerTransactionConfigsFetching
          }
          options={configOptions}
          showSearch
          optionFilterProp="text"
          // filterOption={true}
          allowClear
          value={providerTransactionConfigId}
          onChange={(value) => {
            form.setFieldValue("providerTransactionConfigId", value);
            form.setFieldValue("status", TransactionProxyStatus.Pending);
            form.setFieldValue(
              "state",
              TransactionProxyState.PendingCustomerApproval,
            );
          }}
          disabled={
            providerTransactionConfigsLoading ||
            providerTransactionConfigsFetching ||
            !providerId
          }
          placeholder={
            providerTransactionConfigsLoading ||
            providerTransactionConfigsFetching
              ? (messages["common.loading"] as string)
              : (messages["common.selectOne"] as string)
          }
        />
      </Form.Item>

      <StyledFlex style={{ justifyContent: "end" }}>
        <Space>
          <Button htmlType="button" onClick={handleReset} disabled={isUpdating}>
            {messages["common.reset"] as string}
          </Button>
          <Button type="primary" htmlType="submit" loading={isUpdating}>
            {messages["common.save"] as string}
          </Button>
        </Space>
      </StyledFlex>
    </Form>
  );
}

export default memo(AdminEditForm);
